import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader } from "../components/loading";
import { OnboardingWizard } from "../components/onboarding/OnboardingWizard";
import { useAnalytics } from "../lib/analytics";
import type { UserDetails } from "../services/userService";
import * as UserService from "../services/userService";
import type { OnboardingAnswers, UserData } from "../types/onboarding";
import styles from "./Onboarding.module.css";

export function Onboarding() {
	const navigate = useNavigate();
	const { logEvent } = useAnalytics();
	const [userData, setUserData] = useState<UserDetails | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const hasInitialized = useRef(false);

	useEffect(() => {
		if (hasInitialized.current) return;

		hasInitialized.current = true;

		const loadUserData = async () => {
			try {
				const data = await UserService.getCurrentUser();
				setUserData(data);
				logEvent("onboarding_page_loaded", undefined, {
					user_type: data?.userType || "unknown",
					is_onboarded: String(data?.isOnboarded),
				});
			} catch (error) {
				console.error("Onboarding - error loading user:", error);
				logEvent("onboarding_page_load_error", undefined, {
					error_message: error instanceof Error ? error.message : "unknown",
				});
			} finally {
				setIsLoading(false);
			}
		};

		loadUserData();
	}, [logEvent]);

	const handleComplete = async (answers: OnboardingAnswers) => {
		// Navigate to thank you page - completion API will be called there
		logEvent("onboarding_wizard_completed", undefined, {
			total_answers: Object.keys(answers).length.toString(),
		});

		navigate("/onboarding/thank-you", { replace: true });
	};

	if (isLoading) {
		return (
			<div className={styles.page}>
				<div className={styles.loadingContainer}>
					<Loader size={128} />
				</div>
			</div>
		);
	}

	return (
		<div className={styles.page}>
			<OnboardingWizard
				userData={userData as UserData | null}
				onComplete={handleComplete}
			/>
		</div>
	);
}
